import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  RocketLaunch as RocketLaunchIcon,
} from '@mui/icons-material';
import { marketingApi } from '../api/marketing';

// "Request Demo Access" — captures a lead and drops the visitor straight into
// the shared read-only demo. The backend records the lead in customer_signups
// and (in demo mode) returns a session + the demo app URL to redirect to.
const RequestDemoPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    companyName: '',
    contactName: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = formData.email.trim();
    if (email.length < 3 || !email.includes('@')) {
      setError('Please enter a valid work email address.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await marketingApi.requestDemo({
        email,
        companyName: formData.companyName.trim() || undefined,
        contactName: formData.contactName.trim() || undefined,
        phone: formData.phone.trim() || undefined,
      });

      // Same-origin fast path: hand the demo app a ready session so it skips its
      // own auto-login round-trip. Harmless cross-origin (the app self-authenticates).
      if (res.access_token) localStorage.setItem('accessToken', res.access_token);
      if (res.refresh_token) localStorage.setItem('refreshToken', res.refresh_token);
      if (res.csrf_token) localStorage.setItem('csrfToken', res.csrf_token);

      setLaunching(true);
      window.location.href = res.redirect_url || '/';
    } catch (err) {
      setError('Something went wrong submitting your request. Please try again.');
      setLoading(false);
    }
  };

  if (launching) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" color="text.secondary">
          Launching your live demo…
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <RocketLaunchIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1 }} />
        <Typography variant="h3" sx={{ mb: 1.5, fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Try the{' '}
          <Box
            component="span"
            sx={{
              background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            live demo
          </Box>
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
          Drop your details and we’ll take you straight into a working, fully-seeded
          Debtornator — including the compliance engine in action. No install, no setup.
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              type="email"
              label="Work email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Company"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Your name"
              name="contactName"
              value={formData.contactName}
              onChange={handleChange}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Phone (optional)"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{ py: 1.5, fontSize: '1.05rem', fontWeight: 600 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Launch the demo →'}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            The demo is read-only and uses entirely fictional data. We’ll keep you
            posted on our Q3 2027 launch.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default RequestDemoPage;
